import { memo, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { categoryDisplayName, visibleRows } from '../../core/channelListDerivation';
import type { ChannelListSection, DisplayChannel } from '../../core/channelListDisplayModels';
import { ChannelMarkRead } from '../../core/channelMarkRead';
import { RelativeTime } from '../../core/relativeTime';
import { AvatarIdentity } from '../../core/avatarIdentity';
import { Icon } from '../../design/icons';
import { t, tf } from '../../i18n/i18n';
import { Avatar } from '../Avatar';
import { mediaUrls } from '../media';
import type { SidebarModel, TeamTile } from '../models/sidebarModel';
import { filterSections } from '../models/sidebarModel';
import { useMinuteClock } from '../hooks';

export interface SidebarProps {
  model: SidebarModel | null;
  teamId: string | null;
  selectedChannelId: string | null;
  onSelectTeam(teamId: string): void;
  onSelectChannel(channelId: string): void;
  onToggleCategory(categoryId: string, collapsed: boolean): void;
  onMarkRead(channelId: string): void;
  onOpenProfile(anchor: DOMRect): void;
  query: string;
  onQuery(q: string): void;
  serverBase: string;
}

export function Sidebar(props: SidebarProps) {
  const { model } = props;
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; row: DisplayChannel } | null>(null);
  const now = useMinuteClock();
  const searchRef = useRef<HTMLInputElement>(null);

  const sections = useMemo(() => (model ? filterSections(model.sections, props.query, unreadOnly) : []), [model, props.query, unreadOnly]);
  const anyUnread = useMemo(() => !!model?.sections.some((s) => s.rows.some((r) => (r.isUnread || r.mentionCount > 0) && !r.isMuted)), [model]);
  const isSearching = props.query.trim() !== '';

  // ⌘K / Ctrl+K: the sidebar field IS the switcher (docs/PARITY.md "Channel switcher").
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('blur', close);
    };
  }, [menu]);

  return (
    <nav className="sidebar" aria-label={t('channelList.title')}>
      <div className="wash" />
      {model && (model.teams.length > 1 || model.isAdmin) ? (
        <TeamStrip teams={model.teams} current={props.teamId} onSelect={props.onSelectTeam} isAdmin={model.isAdmin} serverBase={props.serverBase} />
      ) : null}
      <div className="sidebar-search">
        <label className="search-field">
          <Icon name="search" size={15} />
          <input
            ref={searchRef}
            type="search"
            value={props.query}
            placeholder={t('mac.searchPlaceholder')}
            onChange={(e) => props.onQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') props.onQuery('');
              if (e.key === 'Enter') {
                const first = sections[0]?.rows[0];
                if (first) {
                  props.onSelectChannel(first.id);
                  props.onQuery('');
                }
              }
            }}
            aria-label={t('mac.searchPlaceholder')}
          />
        </label>
      </div>
      <div className="chips" role="toolbar">
        <button className="chip" aria-pressed={unreadOnly} title={t('channelList.chip.unread')} onClick={() => setUnreadOnly(!unreadOnly)}>
          <span className="chip-glyph">
            <Icon name="envelope" size={16} />
            {anyUnread ? <span className="unread-dot" /> : null}
          </span>
          <span>{t('channelList.chip.unread')}</span>
        </button>
      </div>
      <div className="channel-list" role="list">
        {!model ? null : model.firstRun ? (
          <FirstRunSkeleton />
        ) : sections.length === 0 ? (
          <div className="empty-hint">{isSearching ? t('channelList.noResults.description') : t('channelList.empty.description')}</div>
        ) : (
          sections.map((section) => (
            <Section
              key={section.id}
              section={section}
              isSearching={isSearching}
              selectedId={props.selectedChannelId}
              now={now}
              onSelect={(id) => {
                props.onSelectChannel(id);
                if (isSearching) props.onQuery('');
              }}
              onToggle={() => props.onToggleCategory(section.id, !section.collapsed)}
              onContext={(e, row) => {
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, row });
              }}
            />
          ))
        )}
      </div>
      {model?.me ? (
        <div className="sidebar-footer">
          <button className="me" onClick={(e) => props.onOpenProfile(e.currentTarget.getBoundingClientRect())} title={t('mac.profileHelp')}>
            <Avatar userId={model.me.id} name={nameOf(model.me)} size={28} version={model.me.lastPictureUpdate} presence={model.myPresence} />
            <span>{nameOf(model.me)}</span>
          </button>
        </div>
      ) : null}
      {menu ? (
        <div className="popover" style={{ left: menu.x, top: menu.y, position: 'fixed' }} role="menu" onClick={(e) => e.stopPropagation()}>
          {ChannelMarkRead.isOffered(menu.row.isUnread, menu.row.mentionCount, menu.row.isMuted) ? (
            <button
              className="menu-item"
              role="menuitem"
              onClick={() => {
                props.onMarkRead(menu.row.id);
                setMenu(null);
              }}
            >
              <Icon name="check" size={16} /> {t('channelList.markRead')}
            </button>
          ) : null}
          <button
            className="menu-item"
            role="menuitem"
            onClick={() => {
              void navigator.clipboard?.writeText(menu.row.name);
              setMenu(null);
            }}
          >
            <Icon name="copy" size={16} /> {menu.row.name}
          </button>
        </div>
      ) : null}
    </nav>
  );
}

function nameOf(u: { firstName: string | null; lastName: string | null; username: string }): string {
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ');
  return full || u.username;
}

function TeamStrip({ teams, current, onSelect, isAdmin, serverBase }: { teams: TeamTile[]; current: string | null; onSelect(id: string): void; isAdmin: boolean; serverBase: string }) {
  return (
    <div className="team-strip">
      {teams.length > 1
        ? teams.map(({ team, mentionCount, hasUnread }) => (
            <button
              key={team.id}
              className="team-tile"
              aria-current={team.id === current}
              title={team.displayName}
              style={{ background: AvatarIdentity.color(team.id) } as CSSProperties}
              onClick={() => onSelect(team.id)}
            >
              {team.lastTeamIconUpdate > 0 ? (
                <img src={mediaUrls.teamIcon(team.id, team.lastTeamIconUpdate)} alt="" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} />
              ) : (
                AvatarIdentity.initials(team.displayName)
              )}
              {team.id !== current && mentionCount > 0 ? <span className="badge">{mentionCount}</span> : team.id !== current && hasUnread ? <span className="dot" /> : null}
            </button>
          ))
        : null}
      {isAdmin ? (
        // The System Console is a gear at the TRAILING end of the teams strip (1.0.277).
        <a className="strip-gear" href={`${serverBase}/admin_console`} target="_blank" rel="noreferrer" title={t('mac.systemConsole.help')} aria-label={t('mac.systemConsole')}>
          <Icon name="gear" size={17} />
        </a>
      ) : null}
    </div>
  );
}

function FirstRunSkeleton() {
  return (
    <div aria-label={t('channelList.firstRun.message')}>
      <div className="empty-hint">{t('channelList.firstRun.message')}</div>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="skeleton-row" style={{ opacity: 1 - i * 0.12 }} />
      ))}
    </div>
  );
}

const Section = memo(function Section({
  section,
  isSearching,
  selectedId,
  now,
  onSelect,
  onToggle,
  onContext,
}: {
  section: ChannelListSection;
  isSearching: boolean;
  selectedId: string | null;
  now: number;
  onSelect(id: string): void;
  onToggle(): void;
  onContext(e: MouseEvent, row: DisplayChannel): void;
}) {
  const rows = visibleRows(section.rows, { collapsed: section.collapsed, isSearching, selectedId });
  return (
    <div role="group" aria-label={categoryDisplayName(section.type, section.displayName)}>
      <button className={`category-header${section.isMuted ? ' muted' : ''}`} aria-expanded={!section.collapsed} onClick={onToggle}>
        <Icon name="chevronDown" size={12} strokeWidth={2} />
        {categoryDisplayName(section.type, section.displayName)}
      </button>
      {rows.map((row) => (
        <Row key={row.id} row={row} selected={row.id === selectedId} now={now} onSelect={onSelect} onContext={onContext} />
      ))}
    </div>
  );
});

const Row = memo(function Row({
  row,
  selected,
  now,
  onSelect,
  onContext,
}: {
  row: DisplayChannel;
  selected: boolean;
  now: number;
  onSelect(id: string): void;
  onContext(e: MouseEvent, row: DisplayChannel): void;
}) {
  const time = row.lastPostAt ? RelativeTime.format(Math.max(row.lastPostAt, row.previewAt), now, { yesterday: t('channelList.time.yesterday') }) : '';
  const classes = ['row'];
  if (row.isUnread && !row.isMuted) classes.push('unread');
  if (row.isMuted) classes.push('muted');
  return (
    <button
      className={classes.join(' ')}
      role="listitem"
      aria-current={selected}
      // The click PAINTS before the conversation is built (1.0.276): selection is local state.
      onMouseDown={(e) => {
        if (e.button === 0) onSelect(row.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(row.id);
      }}
      onContextMenu={(e) => onContext(e, row)}
      title={row.dmPartnerCustomStatusText || undefined}
      data-channel-id={row.id}
    >
      <span className="leading">
        <Leading row={row} />
      </span>
      <span className="text">
        <span className="name">
          {row.name}
          {row.dmPartnerCustomStatusEmoji ? <span className="custom-status-emoji">{row.dmPartnerCustomStatusEmoji}</span> : null}
          {row.isMuted ? <Icon name="bellSlash" size={12} style={{ marginLeft: 6, verticalAlign: -1 }} title={t('channelList.muted.accessibilityLabel')} /> : null}
        </span>
        {row.preview ? (
          <span className="preview">
            {row.previewIsThreadReply ? '↳ ' : ''}
            {row.preview}
          </span>
        ) : null}
      </span>
      <span className="trailing">
        {time ? <span className="time">{time}</span> : null}
        {row.mentionCount > 0 ? (
          <span className="badge" aria-label={tf('channel.jump.newBelow', row.mentionCount)}>
            {row.mentionCount}
          </span>
        ) : row.isUnread && !row.isMuted ? (
          <span className="unread-dot-row" />
        ) : null}
      </span>
    </button>
  );
});

function Leading({ row }: { row: DisplayChannel }) {
  if (row.type === 'D') {
    return <Avatar userId={row.dmPartnerId ?? row.id} name={row.dmPartnerName ?? row.name} version={row.dmPartnerLastPictureUpdate} presence={row.dmPartnerPresence} size={32} />;
  }
  if (row.type === 'G') {
    const faces = row.groupFaces;
    if (!faces.length) return <Icon name="threads" size={18} />;
    // Up to three faces, in the order the title names them; never "+N" (VISUAL-DESIGN §5).
    const layout =
      faces.length === 1
        ? [{ x: 4, y: 4, s: 24 }]
        : faces.length === 2
          ? [
              { x: 0, y: 0, s: 22 },
              { x: 10, y: 10, s: 22 },
            ]
          : [
              { x: 7, y: 0, s: 18 },
              { x: 0, y: 14, s: 18 },
              { x: 14, y: 14, s: 18 },
            ];
    return (
      <span className="group-faces">
        {faces.slice(0, 3).map((f, i) => (
          <span key={f.userId} style={{ position: 'absolute', left: layout[i].x, top: layout[i].y }}>
            <Avatar userId={f.userId} name={f.displayName} size={layout[i].s} version={f.lastPictureUpdate} />
          </span>
        ))}
      </span>
    );
  }
  return <Icon name={row.type === 'P' ? 'lock' : 'hash'} size={17} />;
}
